import {lazy} from 'react'

import {PrivateRoute} from 'components/HOCs'
import {allValidRoles, CASBRoutesEnum, ValidUserTypes} from 'interfaces'
import {componentLoader} from 'utils'
import {DashboardLayout, DefaultLayout, EmptyLayout} from 'components'

const OnboardingPage = lazy(() =>
  componentLoader(
    () =>
      import(/* webpackChunkName: "Onboarding" */ 'features/User/Onboarding'),
  ),
)

const EndpointsPage = lazy(() =>
  componentLoader(
    () => import(/* webpackChunkName: "Endpoints" */ 'features/Endpoints'),
  ),
)

const CASBPage = lazy(() =>
  componentLoader(() => import(/* webpackChunkName: "CASB" */ 'features/CASB')),
)
const CASBAccessPage = lazy(() =>
  componentLoader(
    () => import(/* webpackChunkName: "CASB_Access" */ 'features/CASB/Access'),
  ),
)
const CASBFiles = lazy(() =>
  componentLoader(
    () => import(/* webpackChunkName: "CASB_Files" */ 'features/CASB/Files'),
  ),
)

//Analytics
const AnalyticsOverviewPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Analytics_Overview" */ 'features/Analytics/Overview'
      ),
  ),
)
const AnalyticsPolicyPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Analytics_Policy" */ 'features/Analytics/Policy'
      ),
  ),
)
const AnalyticsProductivityPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Analytics_Productivity" */ 'features/Analytics/Productivity'
      ),
  ),
)
const AnalyticsShadowITPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Analytics_ShadowIT" */ 'features/Analytics/ShadowIT'
      ),
  ),
)

const AnalyticsDetailPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Analytics_Details" */ 'features/Analytics/Detail'
      ),
  ),
)

// policies Routes
const PoliciesListPage = lazy(() =>
  componentLoader(
    () => import(/* webpackChunkName: "Policy_List" */ 'features/Policies'),
  ),
)
const PoliciesBypassPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Policy_Bypass" */ 'features/Policies/Bypass'
      ),
  ),
)

const PoliciesUsersPage = lazy(() =>
  componentLoader(
    () =>
      import(/* webpackChunkName: "Policy_Users" */ 'features/Policies/Users'),
  ),
)
const PoliciesCloudAppPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Policy_CloudApp" */ 'features/Policies/CloudApp'
      ),
  ),
)

const PoliciesContentPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Policy_Content" */ 'features/Policies/Content'
      ),
  ),
)

// settings Admin Routes
const SettingsPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_Admin" */ 'features/Admin/Settings'
      ),
  ),
)
const SettingsAuditLogPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_AuditLog" */ 'features/Settings/AuditLog'
      ),
  ),
)
const SettingsBlockPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_BlockPages" */ 'features/Settings/BlockPages'
      ),
  ),
)
const SettingsEndpointsPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_Endpoints" */ 'features/Settings/Endpoints'
      ),
  ),
)
const SettingsGeneralPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_General" */ 'features/Settings/General'
      ),
  ),
)
const SettingsUsersPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_Users" */ 'features/Settings/Users'
      ),
  ),
)

const SettingsSIEMPage = lazy(() =>
  componentLoader(
    () =>
      import(/* webpackChunkName: "Settings_SIEM" */ 'features/Settings/SIEM'),
  ),
)

const SettingsAPIPage = lazy(() =>
  componentLoader(
    () =>
      import(/* webpackChunkName: "Settings_API" */ 'features/Settings/API'),
  ),
)

const BillingPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Settings_Billing" */ 'features/Settings/Billing'
      ),
  ),
)

const ImportCallbackPage = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: "Import_Callback" */ 'features/ImportCallbackInfo'
      ),
  ),
)

const RouterList = [
  <PrivateRoute
    exact
    path="/onboarding"
    key="/onboarding"
    component={OnboardingPage}
    layoutProps={{
      bgColor: 'bg-dope-black',
    }}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/policies"
    key="/policies"
    component={PoliciesListPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/policies/users"
    key="/policies/users"
    component={PoliciesUsersPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/policies/cloudapp"
    key="/policies/cloudApp"
    component={PoliciesCloudAppPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/policies/content"
    key="/policies/content"
    component={PoliciesContentPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/endpoints"
    key="/endpoints"
    component={EndpointsPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/analytics/overview"
    key="/analytics/overview"
    component={AnalyticsOverviewPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/analytics/policy"
    key="/analytics/policy"
    component={AnalyticsPolicyPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/analytics/productivity"
    key="/analytics/productivity"
    component={AnalyticsProductivityPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/analytics/shadowit"
    key="/analytics/shadowit"
    component={AnalyticsShadowITPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/analytics/detail"
    key="/analytics/detail"
    component={AnalyticsDetailPage}
    layout={DashboardLayout}
    authorizedRoles={allValidRoles}
  />,

  <PrivateRoute
    exact
    path={['/settings', '/settings/grant-access']}
    key="/settings/grant-access"
    component={SettingsPage}
    layout={DefaultLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/settings/general"
    key="/settings/general"
    component={SettingsGeneralPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator]}
  />,
  <PrivateRoute
    exact
    path="/settings/audit-log"
    key="/settings/audit-log"
    component={SettingsAuditLogPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,

  <PrivateRoute
    exact
    path="/settings/block-pages"
    key="/settings/block-pages"
    component={SettingsBlockPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/settings/endpoints"
    key="/settings/endpoints"
    component={SettingsEndpointsPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/settings/users"
    key="/settings/users"
    component={SettingsUsersPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/settings/api"
    key="/settings/api"
    component={SettingsAPIPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/settings/siem"
    key="/settings/siem"
    component={SettingsSIEMPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
  <PrivateRoute
    exact
    path="/import-callback"
    key="/import-callback"
    component={ImportCallbackPage}
    layout={DefaultLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/settings/billing"
    key="/settings/billing"
    component={BillingPage}
    layout={DashboardLayout}
    authorizedRoles={[ValidUserTypes.Administrator]}
  />,
  <PrivateRoute
    exact
    path={CASBRoutesEnum.BASE}
    key={CASBRoutesEnum.BASE}
    component={CASBPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path={[
      CASBRoutesEnum.GRANT_ACCESS,
      CASBRoutesEnum.ACCESS_GRANTED,
      CASBRoutesEnum.ACCESS_DENIED,
      CASBRoutesEnum.SYNC_FAILURE,
    ]}
    key={CASBRoutesEnum.GRANT_ACCESS}
    component={CASBAccessPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path={CASBRoutesEnum.OVERVIEW}
    key={CASBRoutesEnum.OVERVIEW}
    component={CASBPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path={CASBRoutesEnum.OVERVIEW_SERVICE}
    key={CASBRoutesEnum.OVERVIEW_SERVICE}
    component={CASBPage}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path={CASBRoutesEnum.FILES}
    key={CASBRoutesEnum.FILES}
    component={CASBFiles}
    layout={EmptyLayout}
    authorizedRoles={allValidRoles}
  />,
  <PrivateRoute
    exact
    path="/policies/bypass"
    key="/policies/bypass"
    component={PoliciesBypassPage}
    layout={EmptyLayout}
    authorizedRoles={[ValidUserTypes.Administrator, ValidUserTypes.User]}
  />,
]

export const PrivateRoutes = (): JSX.Element[] => {
  return RouterList
}
